import { TFunction } from "i18next";
import * as Yup from "yup";

import { nameof } from "@/common/helper/commonHelper";
import { ZipCodePattern } from "@/common/helper/validationPatterns";

import {
  AmsMappingFormValue,
  AmsParametersFormValues,
  AmsSidMinValue,
  AmsVidMaxLength,
  AmsZipCodeMaxLength,
} from "./AmsParametersSettings.types";

const SidPattern = /^\d+$/;

type AmsMappingField = keyof Pick<AmsMappingFormValue, "zipCode" | "vid">;

/**
 * Validation for the AMS parameters. Mirrors the server rules so the Supervisor gets instant
 * feedback; the server remains the source of truth. PLZ and VID must each be unique across the
 * mapping (1:1 relationship); an incomplete mapping is allowed.
 */
export function amsParametersValidationSchema(t: TFunction) {
  const mappingsField = nameof<AmsParametersFormValues>("mappings");

  const firstDuplicateError = (
    values: Array<Partial<Pick<AmsMappingFormValue, AmsMappingField>>> | undefined,
    field: AmsMappingField,
    message: string,
    createError: (params: { path?: string; message?: string }) => Yup.ValidationError
  ): Yup.ValidationError | true => {
    const seen = new Set<string>();
    for (const [index, entry] of (values ?? []).entries()) {
      const value = (entry[field] ?? "").trim();
      if (!value) {
        continue;
      }
      if (seen.has(value)) {
        return createError({ path: `${mappingsField}.${index}.${field}`, message });
      }
      seen.add(value);
    }
    return true;
  };

  return Yup.object().shape({
    sid: Yup.string()
      .trim()
      .required(t("common.validation.fieldRequired", { fieldName: t("settings.amsParameters.sid") }))
      .matches(SidPattern, t("common.validation.invalidNumber"))
      .test(
        "sid-min-value",
        t("common.validation.minValue", { min: AmsSidMinValue }),
        (sid) => Number(sid.trim()) >= AmsSidMinValue
      ),
    mappings: Yup.array()
      .of(
        Yup.object().shape({
          zipCode: Yup.string()
            .trim()
            .required(t("common.validation.fieldRequired", { fieldName: t("settings.amsParameters.zipCode") }))
            .matches(ZipCodePattern, t("settings.amsParameters.validation.zipCodeInvalid"))
            .max(AmsZipCodeMaxLength, ({ max }) => t("common.validation.maxLength", { max })),
          vid: Yup.string()
            .trim()
            .required(t("common.validation.fieldRequired", { fieldName: t("settings.amsParameters.vid") }))
            .max(AmsVidMaxLength, ({ max }) => t("common.validation.maxLength", { max })),
        })
      )
      .test("unique-zip-code", "", function uniqueZipCode(mappings) {
        return firstDuplicateError(
          mappings,
          "zipCode",
          t("settings.amsParameters.validation.zipCodeUnique"),
          (params) => this.createError(params)
        );
      })
      .test("unique-vid", "", function uniqueVid(mappings) {
        return firstDuplicateError(mappings, "vid", t("settings.amsParameters.validation.vidUnique"), (params) =>
          this.createError(params)
        );
      }),
  });
}
